import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { S26 } from '@sorare/core/src/atoms/icons/S26';
import { SimpleRadio } from '@sorare/core/src/atoms/inputs/SimpleRadio';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXL, BrandXS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import {
  MonetaryAmount,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { LimitedPlusMode_conversionCredit } from './__generated__/index.graphql';
import players from './assets/players.png';
import stadium from './assets/stadium-background.jpg';

const Root = styled.label`
  width: 100%;
  position: relative;
  max-width: calc(80 * var(--unit));
  & > * {
    width: 100%;
  }
  transition: all 0.1s ease-in-out;
  &:hover {
    transform: scale(1.02);
  }
  cursor: pointer;
  color: var(--c-white);
  overflow: hidden;
`;

const RadioWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  color: var(--c-nd-600);

  &.hidden {
    visibility: hidden;
  }
`;

const PlayersImg = styled.img`
  width: 120%;
  max-height: 23vh;
  object-fit: cover;
  object-position: top;
  @media ${tabletAndAbove} {
    max-height: unset;
  }

  @media ${tabletAndAbove} {
    width: 80%;
  }
`;

const StadiumBackground = styled.img`
  position: absolute;
  inset: 0;
  object-fit: contain;
  width: 100%;
  z-index: -1;
  @media ${tabletAndAbove} {
    inset: auto 0 0 0;
  }
`;

const BottomBanner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--quadruple-unit);
  width: 100%;
  color: var(--c-limited-tertiary);
  background: var(--c-limited);
`;

const InnerWrapper = styled(Vertical)`
  position: relative;
  z-index: 0;
  padding: var(--double-unit) 0 0;
`;

const HeaderChip = styled(Horizontal)`
  --color: var(--c-blue-400);
  border-radius: var(--unit);
  box-shadow:
    0 0 12px 0 rgba(var(--c-rgb-blue-400), 0.5) inset,
    0 0 8px 0 var(--color);
  padding: var(--unit) var(--intermediate-unit);
  border: 1px solid var(--color);
`;

const TitleWrapper = styled.div`
  text-wrap: balance;
  text-align: center;
`;

const StyledBrandXS = styled(BrandXS)`
  line-height: 30px;
`;

type Props = {
  selected: boolean;
  goProMode: boolean;
  onGoPro: () => void;
  onSelect: () => void;
  conversionCredit: Nullable<LimitedPlusMode_conversionCredit>;
};

/*
 * just to display something rounded no matter the currency
 */
export const ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY = 200000;

export const LimitedPlusMode = ({
  selected,
  goProMode,
  onGoPro,
  onSelect,
  conversionCredit,
}: Props) => {
  const { main: maxDiscountWithConversion } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || MonetaryAmount.zero,
    hideFractionDigits: true,
  });

  return (
    <Root className={classNames({ selected })}>
      <Bezel
        borderColor={selected ? 'var(--c-limited)' : 'var(--c-nd-150)'}
        className={classNames({ selected })}
      >
        <InnerWrapper center gap={5}>
          <StadiumBackground src={stadium} alt="" />
          <NeonEdge color="var(--c-blue-400)">
            <HeaderChip>
              <RewardConfigImage
                size={3}
                anyRewardConfig={{
                  __typename: 'MonetaryRewardConfig',
                  id: 'monetaryRewardConfig',
                }}
              />
              <BrandS>
                <FormattedMessage
                  id="ChooseGameMode.cashRewards"
                  defaultMessage="Cash rewards*"
                />
              </BrandS>
            </HeaderChip>
          </NeonEdge>

          <Vertical center gap={0.5}>
            <TitleWrapper>
              {!!conversionCredit && (
                <FormattedMessage
                  id="ChooseGameMode.discountMessage"
                  defaultMessage="<a>Up to</a><b>{amount} off*</b><c>on your first card purchases</c>"
                  values={{
                    amount: maxDiscountWithConversion,
                    a: chunks => <BrandXS>{chunks}</BrandXS>,
                    b: chunks => (
                      <BrandXL
                        className="text-gradient-color"
                        style={
                          {
                            '--text-gradient-color':
                              'linear-gradient(262deg, #F1FF98 5.3%, #87F6AB 91.08%)',
                          } as CSSProperties
                        }
                      >
                        {chunks}
                      </BrandXL>
                    ),
                    c: chunks => <BrandS>{chunks}</BrandS>,
                  }}
                />
              )}
            </TitleWrapper>
            <PlayersImg src={players} alt="" />
          </Vertical>
          <RadioWrapper
            className={classNames({
              hidden: goProMode,
            })}
          >
            <SimpleRadio
              id="limited-radio"
              checked={selected}
              checkedColor="var(--c-white)"
              onClick={() => {
                if (goProMode) {
                  onGoPro();
                } else if (!selected) {
                  onSelect();
                }
              }}
            />
          </RadioWrapper>
          <BottomBanner>
            <Marquee gap={1} duration="3s" disableAnimation={!selected}>
              <Horizontal center gap={0.5}>
                <S26 />
                <StyledBrandXS>Pro</StyledBrandXS>
              </Horizontal>
            </Marquee>
          </BottomBanner>
        </InnerWrapper>
      </Bezel>
    </Root>
  );
};

LimitedPlusMode.fragments = {
  conversionCredit: gql`
    fragment LimitedPlusMode_conversionCredit on ConversionCredit {
      id
      maxDiscount {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<LimitedPlusMode_conversionCredit>,
};
